import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { TimePrecision } from '@blueprintjs/datetime';
import moment from 'moment';
import { formAriaProps, useFocusSource, useFocusFirstItem } from 'core/util/ariaUtils';

import whitelistFieldProps from 'core/form/components/util/whitelistFieldProps';
import { DOWN, F6 } from 'core/util/keyCodes';

import DateRangeInputShortcuts from './DateRangeInputShortcuts';

function getDefaultShortcuts() {
  return [
    {
      label: 'Today',
      includeTime: true,
      dateRange: [moment().startOf('day').toDate(), moment().endOf('day').toDate()]
    },
    {
      label: 'This week',
      includeTime: true,
      dateRange: [moment().startOf('week').toDate(), moment().endOf('day').toDate()]
    },
    {
      label: 'This month',
      includeTime: true,
      dateRange: [moment().startOf('month').toDate(), moment().endOf('day').toDate()]
    },
    {
      label: 'Last month',
      includeTime: true,
      dateRange: [
        moment().subtract(1, 'months').startOf('month').toDate(),
        moment().subtract(1, 'months').endOf('month').toDate()
      ]
    },
    {
      label: 'Last 7 days',
      includeTime: true,
      dateRange: [moment().subtract(7, 'days').startOf('day').toDate(), moment().endOf('day').toDate()]
    },
    {
      label: 'Last 30 days',
      includeTime: true,
      dateRange: [moment().subtract(30, 'days').startOf('day').toDate(), moment().endOf('day').toDate()]
    }
  ];
}

const DateRange = (props) => {
  const {
    startField,
    endField,
    form,
    format,
    style,
    small,
    shortcuts,
    asStrings,
    closeOnSelection,
    onChange,
    popoverProps
  } = props;

  const isFocusFromKeyboard = useFocusSource();
  const { focusFirstItem, containerRef } = useFocusFirstItem({ selector: '[role="menuitem"]' });

  const handleInputKeyDown = (event) => {
    const key = event.keyCode ? event.keyCode : event.which;
    if ((key === DOWN || key === F6) && isFocusFromKeyboard) {
      focusFirstItem();
      event.preventDefault();
    }
  };

  useEffect(() => {
    // Until we make the popover properly accessible, we need to hide it from screen readers (per consultants)
    if (containerRef.current) {
      const popover = containerRef?.current?.querySelectorAll('.bp4-popover');
      if (popover?.length) {
        popover[0].setAttribute('aria-hidden', 'true');
      }
    }
  }, []);

  const startFieldState = form.getField(startField);
  const endFieldState = form.getField(endField);

  const { value: start, fieldProps } = startFieldState.getProps();
  const end = endFieldState.getValue();

  let value = [start, end];
  if (asStrings) {
    value = value.map((val) => moment(val, format).toDate());
  }

  popoverProps.usePortal = false; // VPAT: usePortal is not accessible, so we disable it for now
  const ariaProps = formAriaProps(props);

  return (
    <div style={style} ref={containerRef} role="dialog">
      <span id="date-range-screen-reader-instructions" style={{ display: 'none' }}>
        {/* // Save this version for when the popover is truely Screen Reader Friendly:
        Down arrow or F6 to enter date range popover. Tab to navigate through date range options */}
        Date Range format is four digit year, two digit month, two digit day, (space) two digit hour, two digit minute.
        Date values seperated by dashes, time values seperated by colons. Hour and minute default to 00 if not
        specified.
      </span>

      <DateRangeInputShortcuts
        {...fieldProps}
        {...whitelistFieldProps(props)}
        shortcuts={shortcuts === true || shortcuts === undefined ? getDefaultShortcuts() : shortcuts}
        closeOnSelection={closeOnSelection || false}
        formatDate={(date) => moment(date).format(format)}
        parseDate={(str) => moment(str, format).toDate()}
        value={value}
        startInputProps={{
          ...ariaProps,
          small,
          style: { width: 'calc(100% + 2px)' },
          onKeyDown: handleInputKeyDown,
          'aria-describedby': isFocusFromKeyboard ? 'date-range-screen-reader-instructions' : undefined,
          'aria-live': 'polite'
        }}
        endInputProps={{
          ...ariaProps,
          small,
          style: { width: 'calc(100% + 2px)', marginLeft: 2 },
          onKeyDown: handleInputKeyDown,
          'aria-describedby': isFocusFromKeyboard ? 'date-range-screen-reader-instructions' : undefined,
          'aria-live': 'polite'
        }}
        onChange={([newStart, newEnd]) => {
          const prevStartValue = startFieldState.getValue();
          const prevEndValue = endFieldState.getValue();

          const newStartValue = asStrings ? moment(newStart).format(format) : newStart;
          const newEndValue = asStrings ? moment(newEnd).format(format) : newEnd;

          startFieldState.setValue(newStartValue);
          endFieldState.setValue(newEndValue);

          if (onChange) {
            if (prevStartValue !== newStartValue) {
              onChange(startFieldState, newStartValue, prevStartValue, form);
            }
            if (prevEndValue !== newEndValue) {
              onChange(endFieldState, newEndValue, prevEndValue, form);
            }
          }
        }}
        selectAllOnFocus={false}
        popoverProps={popoverProps}
      />
    </div>
  );
};

DateRange.defaultProps = {
  format: 'YYYY-MM-DD HH:mm',
  timePrecision: TimePrecision.MINUTE
};

export default observer(DateRange);
